import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed. Please try again.");
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0B1F3A] px-5 py-16">
      <div className="absolute inset-0 grid-bg" aria-hidden="true" />
      <div className="absolute inset-0 hero-glow" aria-hidden="true" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="mb-8 flex justify-center" data-testid="admin-login-logo">
          <Logo />
        </Link>

        <div className="rounded-2xl border border-white/10 bg-[#0F284B]/80 p-8 shadow-2xl backdrop-blur-xl" data-testid="admin-login-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600/20 text-blue-400">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <h1 className="font-display text-xl font-bold text-white">Staff sign in</h1>
              <p className="text-xs text-slate-400">LKTH LTD internal enquiry dashboard</p>
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="admin-email" className="mb-1.5 block text-sm font-medium text-slate-300">Email</label>
              <input
                id="admin-email"
                type="email"
                required
                autoComplete="username"
                data-testid="admin-email-input"
                className={inputCls}
                placeholder="admin@lkth.co.uk"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-1.5 block text-sm font-medium text-slate-300">Password</label>
              <input
                id="admin-password"
                type="password"
                required
                autoComplete="current-password"
                data-testid="admin-password-input"
                className={inputCls}
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && (
              <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert" data-testid="admin-login-error">
                {error}
              </p>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="admin-login-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-500">
            Authorised LKTH staff only. All sign-in attempts are logged.
          </p>
        </div>

        <Link to="/" className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-400 transition-colors hover:text-white" data-testid="admin-login-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to website
        </Link>
      </div>
    </div>
  );
}
